import { useCallback, useSyncExternalStore } from 'react';
import type { AttachmentState } from '../components/AttachmentPill';
import { displayWorkspaceFileName } from './artifacts';

export type ChatDraft = {
  text: string;
  attachments: AttachmentState[];
  editTarget: {
    messageId: string;
    attachmentsKnown: boolean;
    previous: { text: string; attachments: AttachmentState[] };
  } | null;
  recovery: { text: string; attachments: AttachmentState[] } | null;
  /** Changes only when text changes, so an old submit cannot clear new typing. */
  textVersion: number;
};

const emptyDraft: ChatDraft = {
  text: '',
  attachments: [],
  editTarget: null,
  recovery: null,
  textVersion: 0,
};
const drafts = new Map<string, ChatDraft>();
const listeners = new Map<string, Set<() => void>>();
let epoch = 0;
let textVersion = 0;
let attachmentId = 0;

export function chatDraftKey(userId: number, sessionId: string): string {
  return `${userId}\0${sessionId}`;
}

export function getDraftEpoch(): number {
  return epoch;
}

export function getChatDraft(key: string): ChatDraft {
  return drafts.get(key) ?? emptyDraft;
}

export function updateChatDraft(
  key: string,
  update: (current: ChatDraft) => ChatDraft,
  expectedEpoch = epoch,
): void {
  if (expectedEpoch !== epoch) return;
  const current = getChatDraft(key);
  const changed = update(current);
  if (changed === current) return;
  const next = {
    ...changed,
    textVersion: changed.text === current.text ? current.textVersion : ++textVersion,
  };
  if (!next.text && !next.attachments.length && !next.editTarget && !next.recovery)
    drafts.delete(key);
  else drafts.set(key, next);
  for (const listener of listeners.get(key) ?? []) listener();
}

export function clearChatDrafts(): void {
  epoch += 1;
  drafts.clear();
  for (const subscribers of listeners.values()) for (const listener of subscribers) listener();
}

/** Re-clicking Edit must not reset an active draft or consume a microphone's reserved slot. */
export function editChatDraft(
  key: string,
  messageId: string,
  text: string,
  paths?: string[],
): void {
  updateChatDraft(key, (current) =>
    current.editTarget?.messageId === messageId
      ? current
      : {
          ...current,
          text,
          attachments: (paths ?? []).map((path, index) => ({
            id: `edit-${messageId}-${index}`,
            name: displayWorkspaceFileName(path),
            path,
            uploading: false,
          })),
          editTarget: {
            messageId,
            attachmentsKnown: paths !== undefined,
            previous: current.editTarget?.previous ?? {
              text: current.text,
              attachments: current.attachments,
            },
          },
        },
  );
}

/** Upload completion also reaches the draft temporarily hidden by message editing. */
export function updateDraftAttachments(
  key: string,
  update: (attachment: AttachmentState) => AttachmentState,
  expectedEpoch: number,
): void {
  updateChatDraft(
    key,
    (current) => ({
      ...current,
      attachments: current.attachments.map(update),
      editTarget: current.editTarget
        ? {
            ...current.editTarget,
            previous: {
              ...current.editTarget.previous,
              attachments: current.editTarget.previous.attachments.map(update),
            },
          }
        : null,
    }),
    expectedEpoch,
  );
}

/** Reserve capacity before any asynchronous upload; recordings keep their only local copy. */
export function addDraftFiles(
  key: string,
  files: File[],
  expectedEpoch: number,
  { reservedSlots = 0, keepLocalFiles = false } = {},
): AttachmentState[] {
  let pending: AttachmentState[] = [];
  updateChatDraft(
    key,
    (current) => {
      if (current.attachments.length + files.length + reservedSlots > 10) return current;
      pending = files.map((file) => ({
        id: `upload-${++attachmentId}`,
        name: file.name,
        size: file.size,
        uploading: true,
        localFile: keepLocalFiles ? file : undefined,
      }));
      return { ...current, attachments: [...current.attachments, ...pending] };
    },
    expectedEpoch,
  );
  return pending;
}

/** Initial upload and explicit retry share the same scope/epoch and completion rules. */
export async function uploadDraftAttachments(
  key: string,
  pending: AttachmentState[],
  files: File[],
  expectedEpoch: number,
  upload: (files: File[]) => Promise<Array<{ path: string; size: number }>>,
): Promise<boolean> {
  if (expectedEpoch !== epoch || pending.length === 0) return false;
  const ids = new Set(pending.map((attachment) => attachment.id));
  updateDraftAttachments(
    key,
    (attachment) =>
      ids.has(attachment.id) ? { ...attachment, uploading: true, error: undefined } : attachment,
    expectedEpoch,
  );
  try {
    const uploaded = await upload(files);
    const byId = new Map(pending.map((attachment, index) => [attachment.id, uploaded[index]]));
    updateDraftAttachments(
      key,
      (attachment) => {
        if (!byId.has(attachment.id)) return attachment;
        const match = byId.get(attachment.id);
        return match
          ? {
              ...attachment,
              uploading: false,
              path: match.path,
              size: match.size,
              localFile: undefined,
            }
          : { ...attachment, uploading: false, error: 'Не удалось загрузить файл.' };
      },
      expectedEpoch,
    );
    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Не удалось загрузить файл.';
    updateDraftAttachments(
      key,
      (attachment) =>
        ids.has(attachment.id) ? { ...attachment, uploading: false, error: message } : attachment,
      expectedEpoch,
    );
    return false;
  }
}

export function useChatDraft(key: string): ChatDraft {
  const subscribe = useCallback(
    (listener: () => void) => {
      let subscribers = listeners.get(key);
      if (!subscribers) {
        subscribers = new Set();
        listeners.set(key, subscribers);
      }
      subscribers.add(listener);
      return () => {
        subscribers?.delete(listener);
        if (subscribers?.size === 0) listeners.delete(key);
      };
    },
    [key],
  );
  const snapshot = useCallback(() => getChatDraft(key), [key]);
  return useSyncExternalStore(subscribe, snapshot, () => emptyDraft);
}

/** Restore a failed /new handoff without overwriting typing done while it was pending. */
export function restoreFailedSend(
  key: string,
  text: string,
  paths: string[],
  expectedEpoch: number,
): void {
  const attachments = paths.map((path) => ({
    id: `recovered-${++attachmentId}`,
    name: displayWorkspaceFileName(path),
    path,
    uploading: false,
  }));
  updateChatDraft(
    key,
    (current) =>
      current.text || current.attachments.length || current.editTarget
        ? { ...current, recovery: { text, attachments } }
        : { ...current, text, attachments, recovery: null },
    expectedEpoch,
  );
}
